<template>
    <VfModal scrolls @close="callback()">
        <template #header>Transfer desk call · {{ extensionLabel }}</template>

        <div class="flex flex-col gap-4">
            <p>{{ remoteLabel }}</p>
            <p v-if="!currentCall && !attempt" role="status">This desk call is no longer available. Refresh to see its current state.</p>

            <template v-if="showTransfer">
                <label class="flex flex-col gap-1">
                    <span>Transfer to extension</span>
                    <VfSmartSelect
                        v-model="selectedExtension"
                        :options="availableExtensions"
                        :formatter="formatNumber"
                        placeholder="Select an extension"
                        :disabled="frozen"
                    />
                </label>
                <p v-if="!availableExtensions.length && !frozen">No other extensions are available for transfer.</p>
                <label class="flex flex-col gap-1">
                    <span>Transfer reason (optional)</span>
                    <input
                        :value="transferReason"
                        data-test="desk-transfer-reason"
                        type="text"
                        maxlength="320"
                        autocomplete="off"
                        placeholder="e.g. Customer needs billing help"
                        :disabled="frozen"
                        @input="changeReason"
                    />
                    <span class="text-sm text-ink-3">{{ reasonCount }}/80 · Available to the recipient in compatible ZynoTalk apps.</span>
                </label>
                <p class="text-sm text-ink-3">Transfer now sends the caller to the selected extension and takes you out of the call.</p>
            </template>

            <p v-if="attempt" :role="attempt.status === 'rejected' ? 'alert' : 'status'" data-test="desk-transfer-status">{{ attempt.message }}</p>
            <p v-if="attempt?.status === 'unknown' && !canReplay" role="status" data-test="desk-transfer-expired">
                The safe confirmation window has expired. The transfer remains unresolved; refresh the calls and check the desk phone before taking
                further action.
            </p>
        </div>

        <template #footer>
            <button type="button" class="zs-btn" data-test="desk-close" @click="callback()">Close</button>
            <button type="button" class="zs-btn" :disabled="refreshing" data-test="desk-refresh" @click="refresh">Refresh</button>
            <button
                v-if="attempt?.status === 'unknown'"
                type="button"
                class="zs-btn zs-btn--primary"
                :disabled="!canReplay"
                data-test="desk-retry"
                @click="retry"
            >
                Check same transfer
            </button>
            <button
                v-else-if="showTransfer"
                type="button"
                class="zs-btn zs-btn--primary"
                :disabled="!canTransfer"
                data-test="desk-transfer"
                @click="transfer"
            >
                {{ attempt?.status === 'sending' ? 'Requesting…' : 'Transfer now' }}
            </button>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { VfModal, VfSmartSelect } from '@zyno-io/vue-foundation';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import type { IExtensionLiveCallResponse, IExtensionResponse } from '@/openapi-clients-generated/talk';

import { canTransferDeskCall } from '@/modules/talk/helpers/extension-live-calls';
import { deskCallTransferMgr, type DeskCallSource } from '@/modules/talk/shared/desk-call-transfer.manager';
import { extensionLiveCallsMgr } from '@/modules/talk/shared/extension-live-calls.manager';
import { useTalkHelpers } from '@/modules/talk/shared/store';
import { DataCache } from '@/shared/data/cache';

const props = defineProps<{
    call: IExtensionLiveCallResponse;
    extensionLabel: string;
    remoteLabel: string;
    callback: () => void;
}>();

// Capture the exact row at modal opening. A feed update must never silently retarget this modal.
const source: DeskCallSource = Object.freeze({
    callId: props.call.callId,
    extensionId: props.call.extensionId,
    controlLegId: props.call.controlLegId ?? ''
});
const { formatNumber } = useTalkHelpers();
const extensions = DataCache.getRef('talkExtensions');
const attempt = computed(() => deskCallTransferMgr.attempt(source));
const frozen = computed(() => !!attempt.value && attempt.value.status !== 'rejected');
const currentCall = computed(() =>
    extensionLiveCallsMgr.calls.value.find(
        call => call.callId === source.callId && call.extensionId === source.extensionId && call.controlLegId === source.controlLegId
    )
);
const showTransfer = computed(() => (!!currentCall.value && canTransferDeskCall(currentCall.value)) || !!attempt.value);
const availableExtensions = computed(() =>
    (extensions.value ?? []).filter(
        ext => ext.id !== source.extensionId && !ext.deletedAt && ext.enableSip && ext.capabilities?.physicalEndpoint === true
    )
);
const selectedExtension = ref<IExtensionResponse | null>(null);
const transferReason = ref('');
const reasonCount = computed(() => Array.from(transferReason.value).length);
const now = ref(Date.now());
const canReplay = computed(() => !!attempt.value && deskCallTransferMgr.canReplay(attempt.value, now.value));
const canTransfer = computed(
    () =>
        !!currentCall.value &&
        canTransferDeskCall(currentCall.value) &&
        !!selectedExtension.value &&
        availableExtensions.value.some(ext => ext.id === selectedExtension.value?.id) &&
        !frozen.value
);
const refreshing = ref(false);

watch(
    attempt,
    value => {
        if (!value) return;
        selectedExtension.value = (extensions.value ?? []).find(ext => ext.id === value.body.destinationExtensionId) ?? null;
        transferReason.value = value.body.transferReason ?? '';
    },
    { immediate: true }
);

let clock: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
    clock = setInterval(() => (now.value = Date.now()), 1000);
});
onUnmounted(() => {
    if (clock !== undefined) clearInterval(clock);
});

function changeReason(event: Event) {
    if (frozen.value) return;
    transferReason.value = Array.from((event.target as HTMLInputElement).value)
        .slice(0, 80)
        .join('');
}

async function refresh() {
    refreshing.value = true;
    try {
        await deskCallTransferMgr.refresh();
    } finally {
        refreshing.value = false;
    }
}

async function transfer() {
    if (!canTransfer.value || !selectedExtension.value) return;
    await deskCallTransferMgr.transfer(source, selectedExtension.value.id, transferReason.value || undefined);
}

async function retry() {
    await deskCallTransferMgr.replay(source);
}
</script>
